import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import Modal from "components/Modal";
import { showModal, hideModal } from "actions/modalActions";
import * as calendarsActions from "actions/calendarsActions";
import * as groupsActions from "actions/groupsActions";
import * as shiftsActions from "actions/shiftsActions";
import * as timesActions from "actions/timesActions";
import * as ubicationsActions from "actions/ubicationsActions";
import ProgressBar from "../../atoms/ProgressBar/ProgressBar";
import OnboardingStepCard from "../../molecules/OnboardingStepCard/OnboardingStepCard";
import OnboardingCloseModal from "../../molecules/OnboardingCloseModal/OnboardingCloseModal";
import {
	getOnboardingSteps,
	SHIFT_QUICK_CREATE_PRESETS,
} from "../../config/onboardingSteps";
import { isHaddockEnvironment } from "utils/documentUtils";
import { onboardingStore } from "../../store/onboardingStore";
import useOnboarding from "../../hooks/useOnboarding";
import {
	createOnboardingModalHandlers,
	openOnboardingCreateModal,
	openOnboardingEditModal,
} from "../../utils/onboardingModals";
import "./OnboardingChecklist.scss";

const buildExpandedState = () =>
	getOnboardingSteps().reduce((acc, step) => {
		acc[step.key] = false;
		return acc;
	}, {});

const OnboardingChecklist = ({
	standalone = false,
	showModal,
	hideModal,
	getWeekdaysDefault,
	getSelectableCalendars,
	getSelectableDayTypes,
	getChildGroups,
	getSelectableTimeTypes,
	getDefaultTimeType,
	getSelectableShifts,
	getSelectableUbications,
	getShiftTypes,
	getDefaultShift,
}) => {
	const { t } = useTranslation();
	const history = useHistory();
	const onboarding = useOnboarding();
	const { steps, completedCount, total, isComplete, checklistVisible } =
		onboarding;
	const onboardingSteps = useMemo(() => getOnboardingSteps(), []);
	const isHaddock = isHaddockEnvironment();
	const [closeModalOpen, setCloseModalOpen] = useState(false);
	const [closeVariant, setCloseVariant] = useState("incomplete");
	const [closing, setClosing] = useState(false);

	const stepStateByKey = useMemo(
		() =>
			steps.reduce((acc, s) => {
				acc[s.key] = s;
				return acc;
			}, {}),
		[steps]
	);

	const [expandedByKey, setExpandedByKey] = useState(buildExpandedState);

	const toggleStep = useCallback((key) => {
		setExpandedByKey((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	}, []);

	const modalHandlers = useMemo(
		() =>
			createOnboardingModalHandlers({
				showModal,
				hideModal,
				t,
				onRefresh: () => onboardingStore.refresh(),
			}),
		[showModal, hideModal, t]
	);

	const getSecondaryActions = useCallback(
		(stepKey) => {
			if (stepKey === "shifts") {
				return SHIFT_QUICK_CREATE_PRESETS.map((preset) => ({
					key: preset.key,
					label: t(`onboarding.steps.shifts.quickCreate.${preset.key}`),
					onClick: () =>
						openOnboardingCreateModal(stepKey, modalHandlers, {
							initialInterval: {
								start: preset.start,
								end: preset.end,
							},
						}),
				}));
			}

			return [];
		},
		[modalHandlers, t]
	);

	const openCloseModal = useCallback((variant) => {
		setCloseVariant(variant);
		setCloseModalOpen(true);
	}, []);

	const handleToggleComplete = useCallback(
		async (stepKey, complete) => {
			const result = await onboarding.setStepComplete(stepKey, complete);
			if (!result.ok) {
				toastr.error(t("common.error"), t("common.errorGeneric"));
				return;
			}
			if (result.justCompletedAll) {
				openCloseModal("complete");
			}
		},
		[onboarding, openCloseModal, t]
	);

	const handleConfirmClose = useCallback(async () => {
		setClosing(true);
		const ok = await onboarding.dismiss();
		setClosing(false);
		if (!ok) {
			toastr.error(t("common.error"), t("common.errorGeneric"));
			return;
		}
		setCloseModalOpen(false);
		if (standalone) {
			history.push("/");
		}
	}, [history, onboarding, standalone, t]);

	const visible = standalone
		? onboardingStore.isEnabled() &&
		  onboarding.flagLoaded &&
		  !onboarding.dismissed
		: checklistVisible;

	useEffect(() => {
		if (visible) {
			if (typeof getWeekdaysDefault === "function") {
				getWeekdaysDefault();
			}
			if (typeof getSelectableCalendars === "function") {
				getSelectableCalendars();
			}
			if (typeof getSelectableDayTypes === "function") {
				getSelectableDayTypes();
			}
			if (typeof getChildGroups === "function") {
				getChildGroups();
			}
			if (typeof getSelectableTimeTypes === "function") {
				getSelectableTimeTypes();
			}
			if (typeof getDefaultTimeType === "function") {
				getDefaultTimeType();
			}
			if (typeof getSelectableShifts === "function") {
				getSelectableShifts();
			}
			if (typeof getSelectableUbications === "function") {
				getSelectableUbications();
			}
			if (typeof getShiftTypes === "function") {
				getShiftTypes();
			}
			if (typeof getDefaultShift === "function") {
				getDefaultShift();
			}
		}
	}, [
		visible,
		getWeekdaysDefault,
		getSelectableCalendars,
		getSelectableDayTypes,
		getChildGroups,
		getSelectableTimeTypes,
		getDefaultTimeType,
		getSelectableShifts,
		getSelectableUbications,
		getShiftTypes,
		getDefaultShift,
	]);

	if (!visible && !closeModalOpen) return null;

	const pct = total > 0 ? (completedCount / total) * 100 : 0;

	return (
		<>
			{visible && (
			<section
				className="onboarding-checklist"
				aria-label={t("onboarding.title")}
			>
				<header className="onboarding-checklist__header">
					<div>
						<h2 className="onboarding-checklist__title">
							{t("onboarding.title")}
						</h2>
						<p className="onboarding-checklist__subtitle">
							{t("onboarding.subtitle")}
						</p>
					</div>
					<button
						type="button"
						className="btn btn-outline-primary onboarding-checklist__close"
						onClick={() =>
							openCloseModal(isComplete ? "complete" : "incomplete")
						}
					>
						{t("onboarding.closeOnboarding")}
					</button>
				</header>

				<div className="onboarding-checklist__progress">
					<div className="onboarding-checklist__progress-row">
						<span className="onboarding-checklist__progress-label">
							{t("onboarding.progress")}
						</span>
						<span className="onboarding-checklist__progress-count">
							{t("onboarding.completedOf", {
								completed: completedCount,
								total,
							})}
						</span>
					</div>
					<ProgressBar
						value={pct}
						tone={isComplete ? "success" : "primary"}
						ariaLabel={t("onboarding.progress")}
					/>
				</div>

				<div className="onboarding-checklist__steps">
					{onboardingSteps.map((step, index) => {
						const stepState = stepStateByKey[step.key] || {
							done: false,
							items: [],
						};

						return (
							<OnboardingStepCard
								key={step.key}
								stepKey={step.key}
								icon={step.icon}
								title={t(`onboarding.steps.${step.key}.title`)}
								description={t(
									step.key === "employees" && isHaddock
										? "onboarding.steps.employees.descHaddock"
										: `onboarding.steps.${step.key}.desc`
								)}
								createCtaLabel={t(
									`onboarding.steps.${step.key}.createCta`
								)}
								navCtaLabel={t(`onboarding.steps.${step.key}.cta`)}
								createdLabel={t(
									`onboarding.steps.${step.key}.createdLabel`,
									{ count: stepState.items.length }
								)}
								to={step.to}
								items={stepState.items}
								secondaryActions={getSecondaryActions(step.key)}
								done={!!stepState.done}
								expanded={!!expandedByKey[step.key]}
								index={index}
								onToggle={() => toggleStep(step.key)}
								onItemClick={(item) =>
									openOnboardingEditModal(
										step.key,
										item,
										modalHandlers
									)
								}
								onCreateClick={() =>
									openOnboardingCreateModal(
										step.key,
										modalHandlers
									)
								}
								markCompleteLabel={t("onboarding.markComplete")}
								unmarkCompleteLabel={t("onboarding.unmarkComplete")}
								onCompleteChange={(complete) =>
									handleToggleComplete(step.key, complete)
								}
							/>
						);
					})}
				</div>
			</section>
			)}

			<OnboardingCloseModal
				isOpen={closeModalOpen}
				variant={closeVariant}
				total={total}
				onCancel={() => setCloseModalOpen(false)}
				onConfirm={handleConfirmClose}
				isLoading={closing}
			/>
			<Modal hideModal={hideModal} />
		</>
	);
};

OnboardingChecklist.propTypes = {
	standalone: PropTypes.bool,
	showModal: PropTypes.func.isRequired,
	hideModal: PropTypes.func.isRequired,
	getWeekdaysDefault: PropTypes.func,
	getSelectableCalendars: PropTypes.func,
	getSelectableDayTypes: PropTypes.func,
	getChildGroups: PropTypes.func,
	getSelectableTimeTypes: PropTypes.func,
	getDefaultTimeType: PropTypes.func,
	getSelectableShifts: PropTypes.func,
	getSelectableUbications: PropTypes.func,
	getShiftTypes: PropTypes.func,
	getDefaultShift: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => ({
	showModal: (modalProps, modalType) =>
		dispatch(showModal({ modalProps, modalType })),
	hideModal: () => dispatch(hideModal()),
	getWeekdaysDefault: () => dispatch(calendarsActions.getWeekdaysDefault()),
	getSelectableCalendars: () =>
		dispatch(calendarsActions.getSelectableCalendars()),
	getSelectableDayTypes: () =>
		dispatch(calendarsActions.getSelectableDayTypes()),
	getChildGroups: () => dispatch(groupsActions.getChildGroups()),
	getSelectableTimeTypes: () =>
		dispatch(timesActions.getSelectableTimeTypes()),
	getDefaultTimeType: () => dispatch(timesActions.getDefaultTimeType()),
	getSelectableShifts: () => dispatch(shiftsActions.getSelectableShifts()),
	getSelectableUbications: (options) =>
		dispatch(ubicationsActions.getSelectableUbications(options)),
	getShiftTypes: () => dispatch(shiftsActions.getShiftTypes()),
	getDefaultShift: () => dispatch(shiftsActions.getDefaultShift()),
});

export default connect(null, mapDispatchToProps)(OnboardingChecklist);
